// hooks/useSupplierInvite.ts
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { decodeToken, isExpired } from "react-jwt";
import { useHistory } from "react-router-dom";
import queryString from "query-string";

import { SupplierInvitePayload } from "../../models/supplier";
import CompanyService from "../../services/company.service";

type DecodedProps = {
  userId: number;
  companyId: number;
  supplierId: number;
};

export interface SupplierInviteState {
  // Estados
  inviteDeclined: boolean;
  companyId: number | undefined;
  userId: number | undefined;
  supplierId: number | undefined;
  newInvitationHasAlreadyBeenMade: boolean;
  token: string;
  isInvalidToken: boolean;
  isExpiredToken: boolean;
  showPasswordPage: boolean;
  
  // Estados da mutation
  isLoadingInviteSupplier: boolean;
  supplierInvited: boolean;
  
  // Computed values
  isExpiredOrInvalidOrRejected: boolean;
}

export interface SupplierInviteActions {
  handleAcceptInvite: () => void;
  handleRejectInvite: () => void;
  handleInviteUserAgain: () => void;
  handleResendInvite: () => void;
  handleSubmit: (passwordEncrypted: string) => void;
  goBackToLogin: () => void;
}

export const useSupplierInvite = () => {
  const history = useHistory();
  const { t: translate } = useTranslation();
  
  // eslint-disable-next-line no-restricted-globals
  const parsed: { token?: string } = queryString.parse(location.search);

  // Estados
  const [inviteDeclined, setInviteDeclined] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<number>();
  const [userId, setUserId] = useState<number>();
  const [supplierId, setSupplierId] = useState<number>();
  const [newInvitationHasAlreadyBeenMade, setNewInvitationHasAlreadyBeenMade] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [isInvalidToken, setIsInvalidToken] = useState<boolean>(false);
  const [isExpiredToken, setIsExpiredToken] = useState<boolean>(false);
  const [showPasswordPage, setShowPasswordPage] = useState<boolean>(false);

  // Mutation
  const {
    mutate: inviteSupplier,
    isLoading: isLoadingInviteSupplier,
    isSuccess: supplierInvited,
  } = useMutation(["company.invite.user"], (supplierPayload: SupplierInvitePayload) =>
    CompanyService.inviteSupplierInCompany(supplierPayload)
  );

  // Computed values
  const isExpiredOrInvalidOrRejected = inviteDeclined || isInvalidToken || isExpiredToken;

  // Actions
  const handleAcceptInvite = () => {
    setShowPasswordPage(true);
  };

  const handleRejectInvite = () => {
    setInviteDeclined(true);
    if (userId && companyId && supplierId)
      inviteSupplier({ supplierId, action: "decline", companyId });
  };

  const handleInviteUserAgain = () => {
    if (!isLoadingInviteSupplier && !newInvitationHasAlreadyBeenMade && companyId && supplierId) {
      setNewInvitationHasAlreadyBeenMade(true);
      inviteSupplier({ supplierId, action: "resend", companyId });
    }
  };

  const handleResendInvite = () => {
    if (userId && companyId && supplierId)
      inviteSupplier({ supplierId, action: "decline", companyId });
  };

  const handleSubmit = (passwordEncrypted: string) => {
    if (userId && companyId && supplierId)
      inviteSupplier({ supplierId, action: "accept", companyId, password: passwordEncrypted });
  };

  const goBackToLogin = () => {
    history.replace("/");
  };

  // Effects
  useEffect(() => {
    if (parsed?.token) {
      setToken(parsed.token);
    } else {
      history.replace("/");
    }
  }, [parsed]);

  useEffect(() => {
    if (token) {
      const decodedToken = decodeToken<DecodedProps>(token);
      const tokenIsExpired = isExpired(token);

      if (!decodedToken?.companyId || !decodedToken?.userId || !decodedToken?.supplierId) {
        setIsInvalidToken(true);
        return;
      }

      if (tokenIsExpired) {
        setIsExpiredToken(true);
      }

      setCompanyId(decodedToken.companyId);
      setUserId(decodedToken.userId);
      setSupplierId(decodedToken.supplierId);
    }
  }, [token]);

  // State object
  const state: SupplierInviteState = {
    inviteDeclined,
    companyId,
    userId,
    supplierId,
    newInvitationHasAlreadyBeenMade,
    token,
    isInvalidToken,
    isExpiredToken,
    showPasswordPage,
    isLoadingInviteSupplier,
    supplierInvited,
    isExpiredOrInvalidOrRejected,
  };

  // Actions object
  const actions: SupplierInviteActions = {
    handleAcceptInvite,
    handleRejectInvite,
    handleInviteUserAgain,
    handleResendInvite,
    handleSubmit,
    goBackToLogin,
  };

  return {
    state,
    actions,
    translate,
  };
};
